import Validator from 'src/helpers/Validator';

export const validateAmount = (value: string, min: number = 1) =>
  new Validator(value)
    .noValue(`Please enter an amount`)
    .noMinValue(`Minimum amount is ${min} PLN`, min)
    .res();

export const validateEmail = (value: string) =>
  new Validator(value)
    .noValue('Please enter an email')
    .illegalCharEmail('Invalid email address')
    .res();

export const validateFirstName = (value: string) =>
  new Validator(value)
    .noValue('Please enter a first name')
    .illegalChar('Invalid character entered')
    .res();

export const validateLastName = (value: string) =>
  new Validator(value)
    .noValue('Please enter a last name')
    .illegalChar('Invalid character entered')
    .res();

export const validateID = (value: string) =>
  new Validator(value)
    .noValue('Please enter an ID number')
    .invalidIdCard('Invalid ID number')
    .res();

export const validatePhone = (value: string) =>
  new Validator(value)
    .noValue('Please enter a phone number')
    .invalidPhone('Entered phone number is invalid')
    .res();

export const validatePostCode = (value: string) =>
  new Validator(value)
    .noValue('Please enter a postcode')
    .invalidPostCode('Invalid postcode')
    .res();

export const validateCity = (value: string) =>
  new Validator(value)
    .noValue('Please enter a city')
    .illegalChar('Invalid character entered')
    .res();

export const validateCountry = (value: string) =>
  new Validator(value).noValue('Please select a country').res();

export const validateNewPassword = (value: string, secondValue?: string) =>
  new Validator(value)
    .noValue('Please enter a new password')
    .invalidPassword(
      'Minimum 8 characters required, uppercase letter, lowercase letter, and a digit'
    )
    .isEqual(
      secondValue ?? '',
      'New password cannot be the same as the previous one'
    )
    .res();

export const validateRepeatPass = (value: string, secondValue: string) =>
  new Validator(value)
    .noValue('Please repeat the new password')
    .notEqual(secondValue, 'Entered passwords differ')
    .res();

export const validateConsent = (value: boolean) => {
  if (!value) {
    return 'Consent is required';
  }
  return null;
};

export const validateAmountBetween = (
  value: string,
  min: number,
  max: number
) =>
  new Validator(value)
    .noValue(`Please enter the debt amount`)
    .noMoreThanZero('The entered amount must be greater than zero')
    .noLessThanMin(
      `The entered amount must be greater than or equal to ${min}`,
      min
    )
    .noMoreThanMax(
      `The entered amount must be less than or equal to ${max}`,
      max
    )
    .res();

export const validateEmpty = (value: string, msg: string) =>
  new Validator(value).noValue(msg).res();

export const validateSelect = (value: string, msg: string) =>
  new Validator(value).noValue(msg).res();

export const validateMultiSelect = (value: string) =>
  new Validator(value).noLength('Please select at least one').res();

export const validateHexColor = (value: string) =>
  value
    ? new Validator(value).invalidPostCode('Invalid hex color').res()
    : undefined;

export const validateHexColorRequired = (value: string) =>
  new Validator(value)
    .noValue('Please enter a hex color')
    .invalidPostCode('Invalid hex color')
    .res();

export const validateDateRange = (
  endDate: Date | null,
  startDate: Date | null,
  endDateRequiredMessage: string = 'End date is required',
  endDateAfterStartMessage: string = 'End date must be after start date'
) => {
  const endDateError = validateEmpty(
    endDate ? endDate.toString() : '',
    endDateRequiredMessage
  );

  if (endDateError) {
    return endDateError;
  }

  const startDateError = validateEmpty(
    startDate ? startDate.toString() : '',
    endDateAfterStartMessage
  );

  if (startDateError) {
    return startDateError;
  }

  const start = new Date(startDate!);
  const end = new Date(endDate!);

  if (end <= start) {
    return endDateAfterStartMessage;
  }

  return null;
};
